import React, { useState, useRef } from 'react';
import { CheckSquareIcon, SquareIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, FlameIcon, MoonIcon, BookOpenIcon } from 'lucide-react';
import { initialBooks, genreColors, formatIcons } from './constants';
import { BookCard } from './BookCard';
// Mobile bottom sheet for spine taps (shared between standalone + series views)
const MobileSpineSheet: React.FC<{
  book: any;
  darkMode: boolean;
  onClose: () => void;
  onViewDetails: (book: any) => void;
}> = ({
  book,
  darkMode,
  onClose,
  onViewDetails
}) => {
  const bg = darkMode ? 'bg-gray-900' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  return <div className="fixed inset-0 z-50 md:hidden" onClick={onClose} data-id="element-10432" data-mp="g03c1rdzmwho">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-10433" data-mp="kyen25g0rd1h" />
      <div className={`absolute bottom-0 left-0 right-0 rounded-t-2xl shadow-2xl border-t ${bg} ${border}`} onClick={e => e.stopPropagation()} data-id="element-10434" data-mp="kyofdzojdwac">
        <div className="flex justify-center pt-3 pb-1" data-id="element-10435" data-mp="wfhx10h89n3h">
          <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-10436" data-mp="sexz3uotyfga" />
        </div>
        <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`} data-id="element-10437" data-mp="ngwev6fo4a38">
          <XIcon className="h-4 w-4" data-id="element-10438" data-mp="1g4a2uf1e1vb7" />
        </button>
        <div className="px-5 pb-6 pt-2" data-id="element-10439" data-mp="1g6ms7d9h77ya">
          <div className="flex gap-4" data-id="element-10440" data-mp="1bd0gbnb4z83j">
            <div className="flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden shadow-md" data-id="element-10441" data-mp="1bbz4vhq7kjla">
              <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" data-id="element-10442" data-mp="16e7c6lhqotg1" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-10443" data-mp="1z2cxb9cpgkge">
              <h3 className={`font-bold text-base leading-tight line-clamp-2 mb-0.5 ${textPrimary}`} data-id="element-10444" data-mp="1z1jnu0md3guk">
                {book.title}
              </h3>
              {(book.series || book.seriesName) && <p className="text-xs text-indigo-500 mb-0.5" data-id="element-10445" data-mp="297uma1nn4dza">
                  {book.series || book.seriesName}
                  {book.seriesNumber ? ` #${book.seriesNumber}` : ''}
                </p>}
              <p className={`text-sm mb-2 ${textSecondary}`} data-id="element-10446" data-mp="1z83wuedxsp62">
                by {book.author}
              </p>
              <div className="flex flex-wrap gap-1 mb-2" data-id="element-10447" data-mp="249t7vu15t2u5">
                {book.genre && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-10448" data-mp="1q9s3w5pllfag">
                    {book.genre}
                  </span>}
                {book.subgenre && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800'}`} data-id="element-10449" data-mp="3oms7aa2yvx1g">
                    {book.subgenre}
                  </span>}
              </div>
              <div className="flex items-center gap-3" data-id="element-10450" data-mp="1mqt0hp4vw709">
                {(book.spiceLevel ?? 0) > 0 && <div className="flex gap-0.5" data-id="element-10451" data-mp="1hyrs4eau70fi">
                    {Array.from({
                  length: 5
                }).map((_, i) => <FlameIcon key={i} className={`h-3 w-3 ${i < book.spiceLevel ? 'text-red-400 fill-red-400' : darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-10452" data-mp="1rpmhvsnaki5p" data-mpi={i} />)}
                  </div>}
                {(book.darknessLevel ?? 0) > 0 && <div className="flex gap-0.5" data-id="element-10453" data-mp="bc5s3c7uuclx">
                    {Array.from({
                  length: 5
                }).map((_, i) => <MoonIcon key={i} className={`h-3 w-3 ${i < book.darknessLevel ? 'text-indigo-400 fill-indigo-400' : darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-10454" data-mp="8r814i3plwqp" data-mpi={i} />)}
                  </div>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-id="element-10455" data-mp="mpy063a3a6hp">
            {book.format && <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-10456" data-mp="3la69m5khvri5">
                {formatIcons[book.format]}
                <span className="ml-0.5" data-id="element-10457" data-mp="3rlfpldq9a6t">{book.format}</span>
              </span>}
            {book.endingType && <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-green-900/60 text-green-200' : 'bg-green-100 text-green-800'}`} data-id="element-10458" data-mp="32gtva64wx51s">
                {book.endingType}
              </span>}
            {book.relationshipType && book.relationshipType !== 'None' && <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-pink-900/60 text-pink-200' : 'bg-pink-100 text-pink-800'}`} data-id="element-10459" data-mp="2uz45acdczxiq">
                {book.relationshipType}
              </span>}
          </div>
          <button onClick={() => {
          onViewDetails(book);
          onClose();
        }} className="mt-4 w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-id="element-10460" data-mp="2gytd960bccvz">
            <BookOpenIcon className="h-4 w-4" data-id="element-10461" data-mp="22aq7nxq6n37p" />
            View My Copy
          </button>
        </div>
      </div>
    </div>;
};
// Mini series carousel for cover view
const SeriesCoverCarousel: React.FC<{
  seriesName: string;
  seriesBooks: any[];
  isEditMode: boolean;
  selectedBooks: string[];
  toggleBookSelection: (id: string) => void;
  navigateToBookDetail: (id: string) => void;
  onCardClick?: (book: any) => void;
  setHoveredBookId: (id: string | null) => void;
  handleDragStart: (id: string) => void;
  handleDragEnter: (id: string) => void;
  handleDragEnd: () => void;
  darkMode: boolean;
}> = ({
  seriesBooks,
  isEditMode,
  selectedBooks,
  toggleBookSelection,
  navigateToBookDetail,
  onCardClick,
  setHoveredBookId,
  handleDragStart,
  handleDragEnter,
  handleDragEnd,
  darkMode
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const checkScroll = () => {
    if (!scrollRef.current) return;
    const {
      scrollLeft,
      scrollWidth,
      clientWidth
    } = scrollRef.current;
    setCanLeft(scrollLeft > 0);
    setCanRight(scrollLeft < scrollWidth - clientWidth - 10);
  };
  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.75;
    scrollRef.current.scrollTo({
      left: scrollRef.current.scrollLeft + (dir === 'left' ? -amount : amount),
      behavior: 'smooth'
    });
    setTimeout(checkScroll, 300);
  };
  return <div className="relative" data-id="element-10462" data-mp="395k4zlff8ypt">
      {canLeft && <button onClick={() => scroll('left')} className={`absolute left-0 top-1/3 -translate-y-1/2 z-10 -translate-x-3 w-8 h-8 rounded-full shadow-lg flex items-center justify-center ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-800 hover:bg-gray-50'}`} data-id="element-10463" data-mp="3478wbe0v4u96">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-10464" data-mp="2qfu8mtw0g4vs" />
        </button>}
      {canRight && <button onClick={() => scroll('right')} className={`absolute right-0 top-1/3 -translate-y-1/2 z-10 translate-x-3 w-8 h-8 rounded-full shadow-lg flex items-center justify-center ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-800 hover:bg-gray-50'}`} data-id="element-10465" data-mp="2lg7wyovtywp3">
          <ChevronRightIcon className="h-4 w-4" data-id="element-10466" data-mp="1v7s0kwfhzk8f" />
        </button>}
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} onScroll={checkScroll} data-id="element-10467" data-mp="1z7ke7njr0gkn">
        {seriesBooks.map(book => {
        const fullBookData = initialBooks.find(b => b.id === book.id);
        const mergedBook = fullBookData ? {
          ...fullBookData,
          ...book,
          owned: book.owned
        } : null;
        return <div key={book.id} className={`flex-shrink-0 w-[120px] sm:w-[150px] md:w-[160px] ${!book.owned ? 'opacity-50 grayscale' : ''}`} data-id="element-10468" data-mp="2myg0k55fqjki">
              {mergedBook ? <BookCard book={mergedBook} isEditMode={isEditMode && book.owned} toggleBookSelection={toggleBookSelection} navigateToBookDetail={navigateToBookDetail} onCardClick={onCardClick} setHoveredBookId={setHoveredBookId} selectedBooks={selectedBooks} darkMode={darkMode} handleDragStart={handleDragStart} handleDragEnter={handleDragEnter} handleDragEnd={handleDragEnd} data-id="element-10469" data-mp="slq9dv6gbogt" /> : <div className="flex flex-col items-center" data-id="element-10470" data-mp="13yywewnqoduc">
                  <div className="w-full aspect-[2/3] bg-center bg-cover rounded-lg grayscale" style={{
              backgroundImage: `url('${book.coverImage}')`
            }} data-id="element-10471" data-mp="13zi2ng411dsj" />
                </div>}
            </div>;
      })}
      </div>
    </div>;
};
interface OwnedShelfViewProps {
  darkMode: boolean;
  seriesFilter: string;
  viewMode: 'spine' | 'cover';
  isEditMode: boolean;
  filteredBooks: any[];
  seriesData: [string, any[]][];
  selectedBooks: string[];
  toggleBookSelection: (id: string) => void;
  navigateToBookDetail: (id: string) => void;
  onCardClick?: (book: any) => void;
  setHoveredBookId: (id: string | null) => void;
  handleDragStart: (id: string) => void;
  handleDragEnter: (id: string) => void;
  handleDragEnd: () => void;
}
export const OwnedShelfView: React.FC<OwnedShelfViewProps> = ({
  darkMode,
  seriesFilter,
  viewMode,
  isEditMode,
  filteredBooks,
  seriesData,
  selectedBooks,
  toggleBookSelection,
  navigateToBookDetail,
  onCardClick,
  setHoveredBookId,
  handleDragStart,
  handleDragEnter,
  handleDragEnd
}) => {
  const [mobileSheetBook, setMobileSheetBook] = useState<any | null>(null);
  const handleSpineTap = (book: any, fullBook?: any) => {
    if (!book.owned && fullBook === undefined) return;
    if (isEditMode) {
      toggleBookSelection(book.id);
      return;
    }
    const bookData = fullBook ?? book;
    if (window.innerWidth < 768) {
      setMobileSheetBook(bookData);
    } else if (onCardClick && fullBook) {
      onCardClick(bookData);
    } else if (onCardClick) {
      onCardClick(bookData);
    } else {
      navigateToBookDetail(book.id);
    }
  };
  return <div data-id="element-10472" data-mp="1k5q64ku6o7yy">
      {seriesFilter === 'standalone' ? <div className="mt-6" data-id="element-10473" data-mp="1o831cfbb0nhh">
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-10474" data-mp="1o7bbzm8avlzn">
            Standalone Books
          </h2>
          {viewMode === 'spine' ? <div className="relative" data-id="element-10475" data-mp="1f6d9579llkin">
              <div className="flex items-end gap-2 overflow-x-auto pb-6 scrollbar-thin" style={{
          minHeight: 260
        }} data-id="element-10476" data-mp="1196qdcovjltf">
                {filteredBooks.map(book => <div key={book.id} onClick={() => handleSpineTap(book)} onMouseEnter={() => !isEditMode && setHoveredBookId(book.id)} onMouseLeave={() => !isEditMode && setHoveredBookId(null)} draggable={isEditMode} onDragStart={() => isEditMode && handleDragStart(book.id)} onDragEnter={() => isEditMode && handleDragEnter(book.id)} onDragEnd={() => isEditMode && handleDragEnd()} onDragOver={e => isEditMode && e.preventDefault()} className={`relative flex flex-col items-center justify-center text-white font-medium rounded transition-transform duration-200 ease-in-out cursor-pointer text-center select-none ${!isEditMode && 'hover:scale-105'}`} style={{
            width: book.edition === 'special' ? 68 : 56,
            height: book.edition === 'special' ? 250 : 220,
            fontSize: book.edition === 'special' ? '1rem' : '0.85rem',
            backgroundColor: genreColors[!book.coverImage ? book.genre : ''] || genreColors.default,
            backgroundImage: book.coverImage ? `url(${book.coverImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            flexShrink: 0,
            marginBottom: 4
          }} title={book.title} data-id="element-10477" data-mp="2vnhf7l5gyfz7">
                    {!book.coverImage && <div className="flex flex-col items-center px-1 w-full h-full justify-center" style={{
              writingMode: 'vertical-rl'
            }} data-id="element-10478" data-mp="2a2wgq8mbtwwl">
                        <div className="w-full truncate" data-id="element-10479" data-mp="2j29p0xypsffq">{book.title}</div>
                        {book.seriesNumber && <div className="mt-2 text-xs font-normal opacity-80" data-id="element-10480" data-mp="2ag8i2vhbwezr">
                            #{book.seriesNumber}
                          </div>}
                      </div>}
                    <div className="absolute bottom-1 left-1 inline-flex items-center justify-center p-1 rounded bg-black bg-opacity-70 text-white text-xs z-10" title={book.format} data-id="element-10481" data-mp="2k4yqoq6vj17q">
                      {formatIcons[book.format]}
                    </div>
                  </div>)}
                <div className={`absolute bottom-0 left-0 right-0 h-2 ${darkMode ? 'bg-indigo-900' : 'bg-indigo-500'} rounded-full`} data-id="element-10482" data-mp="21fiqk3z3paru" />
              </div>
            </div> : <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-6" data-id="element-10483" data-mp="3cu5wma8nqt30">
              {filteredBooks.map(book => <BookCard key={book.id} book={book} isEditMode={isEditMode} toggleBookSelection={toggleBookSelection} navigateToBookDetail={navigateToBookDetail} onCardClick={onCardClick} setHoveredBookId={setHoveredBookId} selectedBooks={selectedBooks} darkMode={darkMode} handleDragStart={handleDragStart} handleDragEnter={handleDragEnter} handleDragEnd={handleDragEnd} data-id="element-10484" data-mp="37y8hg794rsrt" />)}
            </div>}
        </div> : <div className="space-y-10 mt-6" data-id="element-10485" data-mp="13m0w15m73mab">
          {seriesData.map(([seriesName, seriesBooks]) => <div key={seriesName} className="mb-8" data-id="element-10486" data-mp="18jj9n9b5qpon">
              <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-10487" data-mp="1waeypyo8b6s7">
                {seriesName}
              </h2>
              {viewMode === 'spine' ? <div className="relative" data-id="element-10488" data-mp="2awcatcz1esje">
                  <div className="flex items-end gap-2 overflow-x-auto pb-6 scrollbar-thin" style={{
            minHeight: 260
          }} data-id="element-10489" data-mp="25yki4gqkj2e5">
                    {seriesBooks.map(book => {
              const fullBookData = initialBooks.find(b => b.id === book.id);
              const spineColor = !book.coverImage ? genreColors[fullBookData?.genre || 'default'] || genreColors.default : undefined;
              return <div key={book.id} onClick={() => book.owned && handleSpineTap(book, fullBookData ? {
                ...fullBookData,
                ...book
              } : book)} onMouseEnter={() => {
                if (book.owned && !isEditMode && fullBookData) setHoveredBookId(fullBookData.id);
              }} onMouseLeave={() => !isEditMode && setHoveredBookId(null)} draggable={isEditMode && book.owned} onDragStart={() => isEditMode && book.owned && handleDragStart(book.id)} onDragEnter={() => isEditMode && book.owned && handleDragEnter(book.id)} onDragEnd={() => isEditMode && book.owned && handleDragEnd()} onDragOver={e => isEditMode && book.owned && e.preventDefault()} className={`relative flex flex-col items-center justify-center text-white font-medium rounded transition-transform duration-200 ease-in-out cursor-pointer text-center select-none ${book.owned && !isEditMode ? 'hover:scale-105' : ''} ${book.owned ? '' : 'opacity-50 grayscale'}`} style={{
                width: fullBookData?.edition === 'special' ? 68 : 56,
                height: fullBookData?.edition === 'special' ? 250 : 220,
                fontSize: fullBookData?.edition === 'special' ? '1rem' : '0.85rem',
                backgroundColor: spineColor,
                backgroundImage: book.coverImage ? `url(${book.coverImage})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                flexShrink: 0,
                marginBottom: 4
              }} title={book.title} data-id="element-10490" data-mp="1db7x4b0232my">
                          {!book.coverImage && <div className="flex flex-col items-center px-1 w-full h-full justify-center" style={{
                  writingMode: 'vertical-rl'
                }} data-id="element-10491" data-mp="1ziad7riyhdc4">
                              <div className="w-full truncate" data-id="element-10492" data-mp="1uh351rcwic4x">
                                {book.title}
                              </div>
                              <div className="mt-2 text-xs font-normal opacity-80" data-id="element-10493" data-mp="krhrqqipbxrv">
                                #{book.seriesNumber}
                              </div>
                            </div>}
                          <div className="absolute bottom-1 left-1 inline-flex items-center justify-center p-1 rounded bg-black bg-opacity-70 text-white text-xs z-10" title={fullBookData?.format || 'paperback'} data-id="element-10494" data-mp="fpirhpi9dgz5">
                            {formatIcons[fullBookData?.format || 'paperback']}
                          </div>
                          {isEditMode && book.owned && <div className="absolute top-1 right-1 z-10" data-id="element-10495" data-mp="ymjwvbfq5j3m">
                              {selectedBooks.includes(book.id) ? <CheckSquareIcon className="h-5 w-5 text-indigo-500 bg-white bg-opacity-90 rounded" data-id="element-10496" data-mp="1r5h6sb414z8t" /> : <SquareIcon className="h-5 w-5 text-white bg-black bg-opacity-50 rounded" data-id="element-10497" data-mp="3bt9sbisyvdcy" />}
                            </div>}
                        </div>;
            })}
                    <div className={`absolute bottom-0 left-0 right-0 h-2 ${darkMode ? 'bg-indigo-900' : 'bg-indigo-500'} rounded-full`} data-id="element-10498" data-mp="3gmurvfa7kfe9" />
                  </div>
                </div> : <SeriesCoverCarousel seriesName={seriesName} seriesBooks={seriesBooks} isEditMode={isEditMode} selectedBooks={selectedBooks} toggleBookSelection={toggleBookSelection} navigateToBookDetail={navigateToBookDetail} onCardClick={onCardClick} setHoveredBookId={setHoveredBookId} handleDragStart={handleDragStart} handleDragEnter={handleDragEnter} handleDragEnd={handleDragEnd} darkMode={darkMode} data-id="element-10499" data-mp="32uod9xmpeggx" />}
            </div>)}
        </div>}

      {/* Mobile spine bottom sheet */}
      {mobileSheetBook && <MobileSpineSheet book={mobileSheetBook} darkMode={darkMode} onClose={() => setMobileSheetBook(null)} onViewDetails={book => {
      onCardClick ? onCardClick(book) : navigateToBookDetail(book.id);
    }} data-id="element-10500" data-mp="z3qp2p8tn7es" />}
    </div>;
};